/**
 * Wave 34 — 10 new capitals/major cities:
 * Luanda 🇦🇴, Kinshasa 🇨🇩, Addis Ababa 🇪🇹, Maputo 🇲🇿,
 * Dakar 🇸🇳, Abidjan 🇨🇮, Lusaka 🇿🇲, Harare 🇿🇼,
 * Tbilisi 🇬🇪, Yerevan 🇦🇲
 */
import type { CityAgencyData } from './city-agency-pages';

const s = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development with React, Node.js, Python, and cloud-native architecture.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, and dashboards using modern TypeScript stacks.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containerization, monitoring, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, ISO 27001, GDPR, HIPAA, PCI-DSS.' },
];
const ind = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, and compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplaces, inventory management, and omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, and HIPAA-compliant applications.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility.' },
];
const faq = (city: string, country: string, flight: string, tz: string) => [
  { question: `Why choose ${city} for software development outsourcing?`, answer: `${city} projects benefit from ${tz} timezone alignment, 60%+ cost savings vs local agencies, and senior engineers fluent in English and local languages.` },
  { question: `What technologies do your ${city} engineers use?`, answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes, TensorFlow, PyTorch — full-stack and specialized.' },
  { question: 'How do you ensure code quality?', answer: 'Mandatory code reviews, CI/CD pipelines, 80%+ test coverage, SonarQube analysis, and bi-weekly sprint demos.' },
  { question: `Can I visit your office from ${city}?`, answer: `Yes! Our Casablanca HQ is ${flight} from ${city}. We welcome client visits anytime for kickoffs and sprint reviews.` },
  { question: 'What engagement models do you offer?', answer: 'Dedicated teams, staff augmentation, and fixed-price projects. Start with 2 developers, scale to 50+.' },
  { question: `Do you comply with ${country} data protection laws?`, answer: 'Yes. Enterprise security standards, GDPR compliant. NDAs signed, encrypted environments, full regulatory compliance.' },
];

export const CITY_AGENCY_PAGES_WAVE34: Record<string, CityAgencyData> = {

  luanda: {
    slug: 'software-development-agency-luanda',
    cityName: 'Luanda',
    countryName: 'Angola',
    flag: '🇦🇴',
    timezone: 'WAT (GMT+1)',
    currency: 'AOA/USD',
    title: { en: 'Software Development Agency Luanda | Call IT Dev', pt: 'Agência Desenvolvimento Software Luanda | Call IT Dev' },
    description: { en: 'Software development agency for Luanda, Angola. SaaS, web, mobile, AI. Portuguese-speaking engineers, from $20/hr. 70% savings.', pt: 'Agência de desenvolvimento de software para Luanda, Angola. SaaS, web, mobile, IA. Engenheiros lusófonos, a partir de 20 $/h.' },
    keywords: ['software development agency Luanda', 'IT outsourcing Angola', 'desenvolvimento software Luanda', 'web development Angola'],
    heroTitle: { en: 'Software Development Agency for Luanda', pt: 'Agência de Desenvolvimento de Software para Luanda' },
    heroSubtitle: { en: 'Portuguese-speaking engineers at nearshore rates. GMT+1 timezone, 70% savings. Your Angola tech partner.', pt: 'Engenheiros lusófonos a preços nearshore. Fuso GMT+1, 70% de economia. Seu parceiro tech em Angola.' },
    whyTitle: { en: 'Why Luanda Companies Choose Call IT Dev', pt: 'Por Que Empresas de Luanda Escolhem a Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Portuguese-Speaking Engineers', description: 'Native Portuguese developers for seamless Angolan market collaboration.' },
      { icon: '⏰', title: 'Same Timezone (GMT+1)', description: 'Identical working hours as Luanda for real-time collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs local and European rates.' },
      { icon: '🌍', title: 'Africa Expertise', description: 'Understanding of African markets, mobile-first solutions, and fintech.' },
      { icon: '📱', title: 'Mobile-First', description: 'Specialized in mobile money, USSD, and mobile-first applications for African markets.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Luanda', 'Angola', '6h', 'WAT (same as CET)'),
    localRate: '$60-100', ourRate: '$20-38', savings: '70%+',
  },

  kinshasa: {
    slug: 'software-development-agency-kinshasa',
    cityName: 'Kinshasa',
    countryName: 'DR Congo',
    flag: '🇨🇩',
    timezone: 'WAT (GMT+1)',
    currency: 'CDF/USD',
    title: { en: 'Software Development Agency Kinshasa | Call IT Dev', fr: 'Agence Développement Informatique Kinshasa | Call IT Dev' },
    description: { en: 'Software development agency for Kinshasa, DR Congo. SaaS, web, mobile, AI. French-speaking engineers, from $20/hr. 70% savings.', fr: 'Agence de développement informatique pour Kinshasa, RDC. SaaS, web, mobile, IA. Ingénieurs francophones, dès 20 $/h.' },
    keywords: ['software development agency Kinshasa', 'IT outsourcing Congo', 'développement informatique Kinshasa', 'agence web RDC'],
    heroTitle: { en: 'Software Development Agency for Kinshasa', fr: 'Agence de Développement Informatique pour Kinshasa' },
    heroSubtitle: { en: 'French-speaking engineers at nearshore rates. GMT+1, 70% savings. Your DRC tech partner.', fr: 'Ingénieurs francophones à tarifs nearshore. GMT+1, 70% d\'économies. Votre partenaire tech en RDC.' },
    whyTitle: { en: 'Why Kinshasa Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Kinshasa Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'French-Speaking Engineers', description: 'Native French developers for seamless Congolese market collaboration.' },
      { icon: '⏰', title: 'Same Timezone (GMT+1)', description: 'Identical working hours for real-time collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs international rates.' },
      { icon: '📱', title: 'Mobile-First Solutions', description: 'Mobile money, USSD, and mobile-first apps for African markets.' },
      { icon: '🌍', title: 'Africa Market Expertise', description: 'Deep understanding of Central African business needs.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Kinshasa', 'DR Congo', '7h', 'WAT (same as CET)'),
    localRate: '$60-100', ourRate: '$20-38', savings: '70%+',
  },

  'addis-ababa': {
    slug: 'software-development-agency-addis-ababa',
    cityName: 'Addis Ababa',
    countryName: 'Ethiopia',
    flag: '🇪🇹',
    timezone: 'EAT (GMT+3)',
    currency: 'ETB/USD',
    title: { en: 'Software Development Agency Addis Ababa | Call IT Dev' },
    description: { en: 'Software development agency for Addis Ababa, Ethiopia. SaaS, web, mobile, AI. English-speaking engineers, from $20/hr. 70% savings vs US/UK.' },
    keywords: ['software development agency Addis Ababa', 'IT outsourcing Ethiopia', 'web development Addis Ababa', 'software company Ethiopia'],
    heroTitle: { en: 'Software Development Agency for Addis Ababa' },
    heroSubtitle: { en: 'English-speaking engineers at competitive rates. GMT+3, 70% savings. Supporting Ethiopia\'s digital transformation.' },
    whyTitle: { en: 'Why Addis Ababa Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Speaking Engineers', description: 'Fluent English developers for international projects.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs $80-150/hr in US/UK.' },
      { icon: '🌍', title: 'AU Hub City', description: 'Addis Ababa hosts the African Union — continental business hub.' },
      { icon: '⏰', title: 'European Overlap', description: 'GMT+3 provides strong overlap with European business hours.' },
      { icon: '🚀', title: 'Digital Ethiopia', description: 'Supporting Ethiopia\'s rapidly growing digital economy.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Addis Ababa', 'Ethiopia', '8h', 'EAT with European overlap'),
    localRate: '$60-120', ourRate: '$20-38', savings: '70%+',
  },

  maputo: {
    slug: 'software-development-agency-maputo',
    cityName: 'Maputo',
    countryName: 'Mozambique',
    flag: '🇲🇿',
    timezone: 'CAT (GMT+2)',
    currency: 'MZN/USD',
    title: { en: 'Software Development Agency Maputo | Call IT Dev', pt: 'Agência Desenvolvimento Software Maputo | Call IT Dev' },
    description: { en: 'Software development agency for Maputo, Mozambique. SaaS, web, mobile, AI. Portuguese-speaking engineers, from $20/hr.', pt: 'Agência de desenvolvimento de software para Maputo, Moçambique. SaaS, web, mobile, IA. Engenheiros lusófonos, a partir de 20 $/h.' },
    keywords: ['software development agency Maputo', 'IT outsourcing Mozambique', 'desenvolvimento software Maputo', 'web development Mozambique'],
    heroTitle: { en: 'Software Development Agency for Maputo', pt: 'Agência de Desenvolvimento de Software para Maputo' },
    heroSubtitle: { en: 'Portuguese-speaking engineers at nearshore rates. GMT+2, 70% savings. Your Mozambique tech partner.', pt: 'Engenheiros lusófonos a preços nearshore. GMT+2, 70% de economia.' },
    whyTitle: { en: 'Why Maputo Companies Choose Call IT Dev', pt: 'Por Que Empresas de Maputo Escolhem a Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Portuguese-Speaking Engineers', description: 'Native Portuguese developers for lusophone Africa markets.' },
      { icon: '⏰', title: 'GMT+2 Timezone', description: '1h ahead of Morocco — near-perfect overlap for collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs international rates.' },
      { icon: '🌍', title: 'Southern Africa Hub', description: 'Supporting Mozambique\'s growing tech and resource economy.' },
      { icon: '📱', title: 'Mobile-First', description: 'Mobile money and fintech solutions for African markets.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Maputo', 'Mozambique', '10h', 'CAT with European overlap'),
    localRate: '$60-100', ourRate: '$20-38', savings: '70%+',
  },

  dakar: {
    slug: 'software-development-agency-dakar',
    cityName: 'Dakar',
    countryName: 'Senegal',
    flag: '🇸🇳',
    timezone: 'GMT (GMT+0)',
    currency: 'XOF/EUR',
    title: { en: 'Software Development Agency Dakar | Call IT Dev', fr: 'Agence Développement Informatique Dakar | Call IT Dev' },
    description: { en: 'Software development agency for Dakar, Senegal. SaaS, web, mobile, AI. French-speaking engineers, from $20/hr. 70% savings.', fr: 'Agence de développement informatique pour Dakar, Sénégal. SaaS, web, mobile, IA. Ingénieurs francophones, dès 20 $/h.' },
    keywords: ['software development agency Dakar', 'IT outsourcing Senegal', 'développement informatique Dakar', 'agence web Sénégal'],
    heroTitle: { en: 'Software Development Agency for Dakar', fr: 'Agence de Développement Informatique pour Dakar' },
    heroSubtitle: { en: 'French-speaking engineers at nearshore rates. GMT timezone, 70% savings. West Africa\'s tech capital.', fr: 'Ingénieurs francophones à tarifs nearshore. Fuseau GMT, 70% d\'économies. Capitale tech de l\'Afrique de l\'Ouest.' },
    whyTitle: { en: 'Why Dakar Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Dakar Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'French-Speaking Engineers', description: 'Native French developers for seamless Senegalese market projects.' },
      { icon: '⏰', title: 'GMT Timezone', description: '1h behind Morocco — near-perfect overlap for collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs European rates.' },
      { icon: '🌍', title: 'West Africa Hub', description: 'Dakar is West Africa\'s leading tech and startup ecosystem.' },
      { icon: '📱', title: 'Mobile Money Expertise', description: 'Orange Money, Wave, and mobile-first fintech solutions.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, OHADA compliance.' },
    ],
    services: s, industries: ind,
    faqs: faq('Dakar', 'Senegal', '4h', 'GMT with full CET overlap'),
    localRate: '$50-90', ourRate: '$20-38', savings: '70%+',
  },

  abidjan: {
    slug: 'software-development-agency-abidjan',
    cityName: 'Abidjan',
    countryName: 'Ivory Coast',
    flag: '🇨🇮',
    timezone: 'GMT (GMT+0)',
    currency: 'XOF/EUR',
    title: { en: 'Software Development Agency Abidjan | Call IT Dev', fr: 'Agence Développement Informatique Abidjan | Call IT Dev' },
    description: { en: 'Software development agency for Abidjan, Ivory Coast. SaaS, web, mobile, AI. French-speaking engineers, from $20/hr.', fr: 'Agence de développement informatique pour Abidjan, Côte d\'Ivoire. SaaS, web, mobile, IA. Ingénieurs francophones, dès 20 $/h.' },
    keywords: ['software development agency Abidjan', 'IT outsourcing Ivory Coast', 'développement informatique Abidjan', 'agence web Côte d\'Ivoire'],
    heroTitle: { en: 'Software Development Agency for Abidjan', fr: 'Agence de Développement Informatique pour Abidjan' },
    heroSubtitle: { en: 'French-speaking engineers at nearshore rates. GMT, 70% savings. Ivory Coast\'s economic capital.', fr: 'Ingénieurs francophones à tarifs nearshore. GMT, 70% d\'économies. Capitale économique ivoirienne.' },
    whyTitle: { en: 'Why Abidjan Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Abidjan Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'French-Speaking Engineers', description: 'Native French developers for Ivorian market projects.' },
      { icon: '⏰', title: 'GMT Timezone', description: '1h behind Morocco — excellent overlap for daily collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs European and US rates.' },
      { icon: '🏦', title: 'UEMOA Financial Hub', description: 'Abidjan hosts BRVM stock exchange and major West African banks.' },
      { icon: '📱', title: 'Mobile & Fintech', description: 'MTN Money, Orange Money, and fintech solutions for UEMOA zone.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, OHADA compliance standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Abidjan', 'Ivory Coast', '4.5h', 'GMT with full CET overlap'),
    localRate: '$50-90', ourRate: '$20-38', savings: '70%+',
  },

  lusaka: {
    slug: 'software-development-agency-lusaka',
    cityName: 'Lusaka',
    countryName: 'Zambia',
    flag: '🇿🇲',
    timezone: 'CAT (GMT+2)',
    currency: 'ZMW/USD',
    title: { en: 'Software Development Agency Lusaka | Call IT Dev' },
    description: { en: 'Software development agency for Lusaka, Zambia. SaaS, web, mobile, AI. English-speaking engineers, from $20/hr. 70% savings.' },
    keywords: ['software development agency Lusaka', 'IT outsourcing Zambia', 'web development Lusaka', 'software company Zambia'],
    heroTitle: { en: 'Software Development Agency for Lusaka' },
    heroSubtitle: { en: 'English-speaking engineers at competitive rates. GMT+2, 70% savings. Your Zambia tech partner.' },
    whyTitle: { en: 'Why Lusaka Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Speaking Engineers', description: 'Fluent English developers for seamless collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs US/UK rates.' },
      { icon: '⏰', title: 'GMT+2 Timezone', description: '1h ahead of Morocco — strong overlap for European clients.' },
      { icon: '🌍', title: 'Southern Africa Hub', description: 'Supporting Zambia\'s growing tech and mining digitalization.' },
      { icon: '📱', title: 'Mobile Solutions', description: 'Mobile money and fintech for Zambian and regional markets.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Lusaka', 'Zambia', '10h', 'CAT with European overlap'),
    localRate: '$60-100', ourRate: '$20-38', savings: '70%+',
  },

  harare: {
    slug: 'software-development-agency-harare',
    cityName: 'Harare',
    countryName: 'Zimbabwe',
    flag: '🇿🇼',
    timezone: 'CAT (GMT+2)',
    currency: 'ZWL/USD',
    title: { en: 'Software Development Agency Harare | Call IT Dev' },
    description: { en: 'Software development agency for Harare, Zimbabwe. SaaS, web, mobile, AI. English-speaking engineers, from $20/hr. 70% savings.' },
    keywords: ['software development agency Harare', 'IT outsourcing Zimbabwe', 'web development Harare', 'software company Zimbabwe'],
    heroTitle: { en: 'Software Development Agency for Harare' },
    heroSubtitle: { en: 'English-speaking engineers at competitive rates. GMT+2, 70% savings. Your Zimbabwe tech partner.' },
    whyTitle: { en: 'Why Harare Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Speaking Engineers', description: 'Fluent English developers for global collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs international rates.' },
      { icon: '⏰', title: 'GMT+2 Timezone', description: '1h ahead of Morocco — strong European overlap.' },
      { icon: '🚀', title: 'Digital Economy', description: 'Supporting Zimbabwe\'s growing fintech and mobile ecosystem.' },
      { icon: '📱', title: 'EcoCash & Mobile', description: 'Expertise in mobile money and digital payment platforms.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Harare', 'Zimbabwe', '10h', 'CAT with European overlap'),
    localRate: '$60-100', ourRate: '$20-38', savings: '70%+',
  },

  tbilisi: {
    slug: 'software-development-agency-tbilisi',
    cityName: 'Tbilisi',
    countryName: 'Georgia',
    flag: '🇬🇪',
    timezone: 'GET (GMT+4)',
    currency: 'GEL/USD',
    title: { en: 'Software Development Agency Tbilisi | Call IT Dev' },
    description: { en: 'Software development agency for Tbilisi, Georgia. SaaS, web, mobile, AI. Multilingual engineers, from $25/hr. 60% savings vs Western Europe.' },
    keywords: ['software development agency Tbilisi', 'IT outsourcing Georgia', 'web development Tbilisi', 'software company Georgia Caucasus'],
    heroTitle: { en: 'Software Development Agency for Tbilisi' },
    heroSubtitle: { en: 'Multilingual engineers at nearshore rates. GMT+4, 60% savings. Georgia\'s booming tech capital.' },
    whyTitle: { en: 'Why Tbilisi Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in English, Russian, and Georgian.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at $25-40/hr vs $80-120/hr in Western Europe.' },
      { icon: '🚀', title: 'Tech Hub', description: 'Tbilisi is a rising Eastern European tech and startup hub.' },
      { icon: '⏰', title: 'GMT+4 Timezone', description: 'Overlap with both European and Middle Eastern business hours.' },
      { icon: '🇪🇺', title: 'EU Association', description: 'Georgia\'s EU Association Agreement ensures regulatory alignment.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Tbilisi', 'Georgia', '6h', 'GET with 5h European overlap'),
    localRate: '$70-110', ourRate: '$25-40', savings: '60%+',
  },

  yerevan: {
    slug: 'software-development-agency-yerevan',
    cityName: 'Yerevan',
    countryName: 'Armenia',
    flag: '🇦🇲',
    timezone: 'AMT (GMT+4)',
    currency: 'AMD/USD',
    title: { en: 'Software Development Agency Yerevan | Call IT Dev' },
    description: { en: 'Software development agency for Yerevan, Armenia. SaaS, web, mobile, AI. Multilingual engineers, from $25/hr. 60% savings vs Western Europe.' },
    keywords: ['software development agency Yerevan', 'IT outsourcing Armenia', 'web development Yerevan', 'software company Armenia'],
    heroTitle: { en: 'Software Development Agency for Yerevan' },
    heroSubtitle: { en: 'Multilingual engineers at nearshore rates. GMT+4, 60% savings. Armenia\'s Silicon Mountains.' },
    whyTitle: { en: 'Why Yerevan Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in English, Russian, and Armenian.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at $25-40/hr vs Western European rates.' },
      { icon: '🚀', title: 'Silicon Mountains', description: 'Yerevan is known as Armenia\'s tech capital with a thriving startup scene.' },
      { icon: '⏰', title: 'GMT+4 Timezone', description: 'Overlap with European and Middle Eastern business hours.' },
      { icon: '🎓', title: 'STEM Excellence', description: 'Armenia produces world-class mathematicians and engineers.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, international security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Yerevan', 'Armenia', '6h', 'AMT with 5h European overlap'),
    localRate: '$70-110', ourRate: '$25-40', savings: '60%+',
  },
};
